import React, { use } from "react";
import { useAtom } from "jotai";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import SelectField from "../../components/SelectField";
import { Gender } from "../../__generated__/graphql";
import { BodyMediumText } from "../../components/FormattedText";
import VStack from "../../ui_atoms/VStack";
import Gap from "../../ui_atoms/Gap";
import { toISODateString } from "../../hooks/date";

const CreateMissionTestWorker = () => {
    const i18n = useI18n();
    const [mission, setMission] = useAtom(newMissionState);
    const { validateScreen } = use(CreateMissionScreensContext);

    // Extract year from existing birthdate or default to 1990
    const [selectedYear, setSelectedYear] = React.useState<number>(() => {
        if (mission.workerInfo?.birthdate) {
            return new Date(mission.workerInfo.birthdate).getFullYear();
        }
        return 1990;
    });

    const [gender, setGender] = React.useState<Gender | undefined>(
        mission.workerInfo?.gender || undefined,
    );

    // Generate birthdate as January 1st of selected year
    const birthdate = React.useMemo(() => {
        return new Date(selectedYear, 0, 1); // January 1st of selected year
    }, [selectedYear]);

    const updateTestWorkerInfo = React.useCallback(() => {
        if (mission.workerInfo && "isTestWorker" in mission.workerInfo) {
            setMission({
                ...mission,
                workerInfo: {
                    ...mission.workerInfo,
                    birthdate: toISODateString(birthdate),
                    gender,
                },
            });
        }
    }, [mission, setMission, birthdate, gender]);

    const validate = React.useCallback(async () => {
        updateTestWorkerInfo();
        await validateScreen("CreateMissionTestWorker");
    }, [validateScreen, updateTestWorkerInfo]);

    const origin = useBubbleStepOrigin("CreateMissionTestWorker");

    const genderOptions = React.useMemo(
        () => [
            { label: i18n.t("user.gender_male"), value: Gender.Male },
            { label: i18n.t("user.gender_female"), value: Gender.Female },
        ],
        [i18n],
    );

    // Generate year options (from 1950 to current year)
    const yearOptions = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear - 16; year >= 1950; year--) {
            years.push({ label: year.toString(), value: year });
        }
        return years;
    }, []);

    const isValid = !!selectedYear && !!gender;

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("next"),
                    onPress: validate,
                    isValid: isValid,
                },
            ]}
            withAssistanceButton
        >
            <Bubble title={i18n.t("screens.user.create_mission_test_worker.title")} origin={origin}>
                <VStack gap={1 / 3}>
                    <BodyMediumText>
                        {i18n.t("screens.user.create_mission_test_worker.description")}
                    </BodyMediumText>
                    <Gap gap={1 / 3} />
                    <BodyMediumText>{i18n.t("user.birth_year")}</BodyMediumText>

                    <SelectField<number>
                        label={i18n.t("user.birth_year")}
                        value={selectedYear}
                        onSelect={(selectItem) => setSelectedYear(selectItem.value)}
                        placeholder={i18n.t("user.birth_year")}
                        data={yearOptions}
                    />
                    <Gap gap={1 / 3} />
                    <BodyMediumText>{i18n.t("user.gender")}</BodyMediumText>
                    <SelectField<Gender>
                        label={i18n.t("user.gender")}
                        value={gender}
                        onSelect={(selectItem) => setGender(selectItem.value)}
                        placeholder={i18n.t("user.gender")}
                        data={genderOptions}
                    />
                </VStack>
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionTestWorker;
